import styled from "styled-components";
import { Container } from "../../styled/CommonStyledComponents";

/**
 * Bento grid showing real product screens (exported as SVG from Figma) in
 * colorful cards, each highlighting one feature — same pattern as the
 * Givingli-style reference: bold color blocks, short copy, screen bleeding
 * off the bottom edge.
 */

interface BentoItem {
  title: string;
  description: string;
  image?: string;
  custom?: "capacity";
  background: string;
  area: string;
}

const CAPACITY_WEEKS = [
  { actual: 58, forecast: 60, breach: false },
  { actual: 62, forecast: 64, breach: false },
  { actual: 68, forecast: 70, breach: false },
  { actual: 74, forecast: 78, breach: false },
  { actual: 88, forecast: 92, breach: true },
  { actual: 80, forecast: 84, breach: false },
  { actual: 76, forecast: 82, breach: false },
  { actual: 70, forecast: 76, breach: false }
];

const CAPACITY_CEILING = 85;

const ITEMS: BentoItem[] = [
  {
    title: "Network Complexity",
    description:
      "Rank accounts by complexity to see where operational effort and value are concentrated.",
    image: "/assets/img/bento/network-complexity-ranking.svg",
    background: "linear-gradient(160deg, #ded4ff 0%, #f3efff 100%)",
    area: "ranking"
  },
  {
    title: "Proactive Alerts",
    description:
      "Catch lane shifts, account risk, and emerging disruptions early — before they turn into real cost.",
    image: "/assets/img/bento/key-metrics.svg",
    background: "linear-gradient(160deg, #ffd7dd 0%, #fff0f2 100%)",
    area: "exceptions"
  },
  {
    title: "Account Intelligence",
    description:
      "See account health and risk in one view — integrated into your existing TMS, ERP, and data warehouse.",
    image: "/assets/img/bento/customers-table.svg",
    background: "linear-gradient(160deg, #d4ecd7 0%, #eef8ef 100%)",
    area: "customers"
  },
  {
    title: "Lane Intelligence",
    description:
      "Map every lane, surface backhaul and triangular opportunities, and quantify the rupiah impact of each.",
    image: "/assets/img/bento/lane-metrics-card.svg",
    background: "linear-gradient(160deg, #fdeab0 0%, #fff8e2 100%)",
    area: "lanes"
  },
  {
    title: "Whitespace Mapping",
    description:
      "Find the gaps in your network that AI can turn into backhaul and cost-saving opportunities.",
    image: "/assets/img/bento/network-coverage-matrix.svg",
    background: "linear-gradient(120deg, #ffd9b8 0%, #fff1e2 100%)",
    area: "coverage"
  },
  {
    title: "Capacity Planning",
    description:
      "Forecast demand against capacity to plan ahead of shortages and procurement needs.",
    custom: "capacity",
    background: "linear-gradient(160deg, #d6e4ff 0%, #eef4ff 100%)",
    area: "capacity"
  }
];

const BentoSection = () => {
  return (
    <Section>
      <Container>
        <Header>
          <Eyebrow>What Majura Shows You</Eyebrow>
          <Headline>Every number, ranked, ready to act on</Headline>
        </Header>

        <Grid>
          {ITEMS.map((item) => (
            <Card key={item.area} $area={item.area} $background={item.background}>
              <CardText>
                <CardTitle>{item.title}</CardTitle>
                <CardDescription>{item.description}</CardDescription>
              </CardText>
              {item.custom === "capacity" ? (
                <CapacityWidget>
                  <CapacityHeader>Volume forecast · Next 8 weeks</CapacityHeader>
                  <CapacityChart>
                    <CapacityCeilingLine style={{ bottom: `${CAPACITY_CEILING}%` }} />
                    {CAPACITY_WEEKS.map((week, index) => (
                      <CapacityBarGroup key={index}>
                        <CapacityBar
                          $height={week.forecast}
                          $tone="forecast"
                          $breach={week.breach}
                        />
                        <CapacityBar $height={week.actual} $tone="actual" $breach={week.breach} />
                      </CapacityBarGroup>
                    ))}
                  </CapacityChart>
                  <CapacityLegend>
                    <span>
                      <LegendDot $tone="actual" /> Actual
                    </span>
                    <span>
                      <LegendDot $tone="forecast" /> Forecast
                    </span>
                    <span>
                      <LegendDot $tone="ceiling" /> Capacity ceiling
                    </span>
                  </CapacityLegend>
                </CapacityWidget>
              ) : (
                <ImageWrap>
                  <img src={item.image} alt={item.title} />
                </ImageWrap>
              )}
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

const Section = styled.div`
  width: 100%;
  padding: 96px 0;
  background: #fbfcff;

  @media (max-width: 1025px) {
    padding: 64px 0;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 48px;
`;

const Eyebrow = styled.p`
  color: #4f79ff;
  font-weight: 600;
  letter-spacing: 1px;
  font-size: 0.9rem;
  text-transform: uppercase;
  margin: 0 0 12px;
`;

const Headline = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  color: #0a1f60;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 1025px) {
    font-size: 1.8rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 280px 280px 340px;
  gap: 24px;
  grid-template-areas:
    "ranking ranking exceptions exceptions"
    "ranking ranking customers lanes"
    "coverage coverage capacity capacity";

  @media (max-width: 1025px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 340px 280px 300px 300px 320px;
    grid-template-areas:
      "ranking ranking"
      "exceptions exceptions"
      "customers lanes"
      "coverage coverage"
      "capacity capacity";
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(6, 320px);
    grid-template-areas:
      "ranking"
      "exceptions"
      "customers"
      "lanes"
      "coverage"
      "capacity";
  }
`;

const Card = styled.div<{ $area: string; $background: string }>`
  grid-area: ${({ $area }) => $area};
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  background: ${({ $background }) => $background};
  padding: 28px 28px 0;
  display: flex;
  flex-direction: column;
`;

const CardText = styled.div`
  position: relative;
  z-index: 2;
  max-width: 90%;
`;

const CardTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #17203a;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #4b5566;
`;

const ImageWrap = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;

  img {
    position: absolute;
    left: 50%;
    bottom: -28px;
    transform: translateX(-50%);
    width: auto;
    max-width: 148%;
    height: auto;
    max-height: calc(100% + 60px);
  }
`;

const CapacityWidget = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const CapacityHeader = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: #4b5566;
  margin-bottom: 10px;
`;

const CapacityChart = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 12px 0;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 12px 12px 0 0;
`;

const CapacityCeilingLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1.5px dashed #e0442f;

  &::after {
    content: "Ceiling";
    position: absolute;
    right: 8px;
    top: -16px;
    font-size: 0.52rem;
    font-weight: 700;
    color: #e0442f;
  }
`;

const CapacityBarGroup = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
  height: 100%;
`;

const CapacityBar = styled.div<{ $height: number; $tone: "actual" | "forecast"; $breach: boolean }>`
  width: 6px;
  height: ${({ $height }) => $height}%;
  border-radius: 3px 3px 0 0;
  background: ${({ $tone, $breach }) =>
    $breach ? "#e0442f" : $tone === "actual" ? "#4c5cf0" : "#b7c2ff"};
  opacity: ${({ $tone }) => ($tone === "forecast" ? 0.6 : 1)};
`;

const CapacityLegend = styled.div`
  display: flex;
  gap: 14px;
  padding: 8px 4px 0;
  font-size: 0.62rem;
  font-weight: 600;
  color: #4b5566;

  span {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const LegendDot = styled.span<{ $tone: "actual" | "forecast" | "ceiling" }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $tone }) =>
    $tone === "ceiling" ? "#e0442f" : $tone === "actual" ? "#4c5cf0" : "#b7c2ff"};
`;

export default BentoSection;
